// v4
// Path: frontend/src/components/QuickBooksSync/StandardMappingsModal.jsx
// Inputs: token, onClose, onSaved
// Outputs: React JSX modal for managing, editing, and resetting standard field mapping presets across source systems and QuickBooks
// Summary: Interface for viewing, configuring, and updating standard field mappings stored in Sync.StandardMappings using the unified FieldMappingDesigner component with source table selection and all 11 canonical QuickBooks landing entities.

import React, { useState, useEffect } from 'react';
import { FieldMappingDesigner } from './FieldMappingDesigner';
import { QUICKBOOKS_TARGET_ENTITIES, getQbEntityMeta, normalizeEntityName } from './quickbooksEntities';

export const StandardMappingsModal = ({ token, onClose, onSaved }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [mappings, setMappings] = useState([]);
    const [sourceSystems, setSourceSystems] = useState(['Passport']);
    const [selectedSystem, setSelectedSystem] = useState('Passport');
    const [selectedEntityType, setSelectedEntityType] = useState('Customer');
    const [selectedMapping, setSelectedMapping] = useState(null);
    const [message, setMessage] = useState(null);

    const canonicalEntities = QUICKBOOKS_TARGET_ENTITIES.map(e => e.entity);

    const fetchMappings = async () => {
        setLoading(true);
        try {
            const res = await fetch(`/api/quickbooks/standard-mappings?sourceSystem=${encodeURIComponent(selectedSystem)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const fetched = Array.isArray(data.mappings) ? data.mappings : [];
                setMappings(fetched);
                if (Array.isArray(data.sourceSystems) && data.sourceSystems.length > 0) {
                    setSourceSystems(data.sourceSystems);
                }

                // Match currently selected entity
                const matched = fetched.find(m => {
                    const norm = normalizeEntityName(m.TargetQBEntity || m.EntityType);
                    return norm.toLowerCase() === selectedEntityType.toLowerCase();
                }) || fetched[0];

                if (matched) {
                    const canonical = normalizeEntityName(matched.TargetQBEntity || matched.EntityType);
                    setSelectedEntityType(canonical);
                    setSelectedMapping(matched);
                } else if (fetched.length === 0) {
                    setSelectedMapping(null);
                }
            }
        } catch (err) {
            console.error('[StandardMappingsModal] Error fetching mappings:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMappings();
    }, [selectedSystem]);

    const handleSelectEntity = (ent) => {
        const canonical = normalizeEntityName(ent);
        setSelectedEntityType(canonical);
        const matched = mappings.find(m => {
            const mNorm = normalizeEntityName(m.TargetQBEntity || m.EntityType);
            return mNorm.toLowerCase() === canonical.toLowerCase();
        });
        const meta = getQbEntityMeta(canonical);
        setSelectedMapping(matched || {
            MappingID: null,
            SourceSystem: selectedSystem,
            TargetSystem: 'QuickBooks',
            EntityType: canonical,
            TargetQBEntity: canonical,
            SourceTable: meta.defaultTable || '',
            MappingName: `Standard ${selectedSystem} ${canonical} -> QBO Preset`,
            Description: `Default field mappings between ${selectedSystem} ${canonical} and QuickBooks Online`,
            IsDefault: true,
            mappings: []
        });
        setMessage(null);
    };

    const handleSavePreset = async (designerPayload) => {
        setSaving(true);
        setMessage(null);
        try {
            const current = selectedMapping || {};
            const payload = {
                mappingId: current.MappingID || undefined,
                sourceSystem: current.SourceSystem || selectedSystem,
                targetSystem: current.TargetSystem || 'QuickBooks',
                entityType: selectedEntityType,
                targetQBEntity: selectedEntityType,
                sourceTable: designerPayload.sourceTable || current.SourceTable || '',
                mappingName: current.MappingName || `Standard ${selectedSystem} ${selectedEntityType} -> QBO`,
                description: current.Description || '',
                isDefault: current.IsDefault !== undefined ? current.IsDefault : true,
                mappings: designerPayload.mappings
            };

            const res = await fetch('/api/quickbooks/standard-mappings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await res.json();
            if (res.ok) {
                setMessage({ type: 'success', text: data.message || 'Standard mapping preset saved!' });
                await fetchMappings();
                if (onSaved) onSaved();
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to save standard mapping.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleResetDefaults = async () => {
        if (!window.confirm('Reset all standard mappings back to canonical defaults? Any customizations will be refreshed.')) return;
        setSaving(true);
        try {
            const res = await fetch('/api/quickbooks/standard-mappings/reset', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) {
                setMessage({ type: 'success', text: 'Standard mappings reset to canonical defaults.' });
                await fetchMappings();
                if (onSaved) onSaved();
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to reset standard mappings.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const meta = getQbEntityMeta(selectedEntityType);

    const activeMappingProp = selectedMapping || {
        MappingID: null,
        SourceSystem: selectedSystem,
        TargetSystem: 'QuickBooks',
        EntityType: selectedEntityType,
        TargetQBEntity: selectedEntityType,
        SourceTable: meta.defaultTable || '',
        MappingName: `Standard ${selectedSystem} ${selectedEntityType} -> QBO Preset`,
        Description: `Default field mappings between ${selectedSystem} ${selectedEntityType} and QuickBooks Online`,
        IsDefault: true,
        mappings: []
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-6xl shadow-2xl flex flex-col h-[92vh] overflow-hidden">
                {/* System & Entity Navigation Bar */}
                <div className="px-6 py-3 bg-slate-850 border-b border-slate-800 flex flex-wrap items-center justify-between gap-4 shrink-0">
                    {/* Source System Selector */}
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                            <span className="material-symbols-outlined text-[15px] text-indigo-400">device_hub</span>
                            Source System:
                        </span>
                        <select
                            value={selectedSystem}
                            onChange={e => setSelectedSystem(e.target.value)}
                            className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-1 text-xs text-white font-semibold focus:border-indigo-500 focus:outline-none"
                        >
                            {sourceSystems.map(sys => (
                                <option key={sys} value={sys}>{sys}</option>
                            ))}
                        </select>
                        <span className="text-slate-500 text-xs">&rarr;</span>
                        <span className="px-2 py-1 bg-emerald-950/60 border border-emerald-800 text-emerald-300 rounded text-xs font-semibold flex items-center gap-1">
                            <span className="material-symbols-outlined text-[13px]">cloud</span>
                            QuickBooks Online
                        </span>
                    </div>

                    {/* Entity Tabs - 11 Canonical Landing Targets */}
                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
                        {canonicalEntities.map(ent => {
                            const isSelected = selectedEntityType.toLowerCase() === ent.toLowerCase();
                            const hasPreset = mappings.some(m => {
                                const norm = normalizeEntityName(m.TargetQBEntity || m.EntityType);
                                return norm.toLowerCase() === ent.toLowerCase();
                            });
                            const entMeta = getQbEntityMeta(ent);

                            return (
                                <button
                                    key={ent}
                                    onClick={() => handleSelectEntity(ent)}
                                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                                        isSelected
                                            ? 'bg-indigo-600 text-white shadow-sm'
                                            : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-750'
                                    }`}
                                >
                                    <span className="material-symbols-outlined text-[14px]">
                                        {entMeta.icon || 'table_chart'}
                                    </span>
                                    {hasPreset && (
                                        <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-emerald-300' : 'bg-emerald-400'}`}></span>
                                    )}
                                    {ent}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Notification Message */}
                {message && (
                    <div className={`px-6 py-2 text-xs border-b flex items-center justify-between gap-2 shrink-0 ${message.type === 'success' ? 'bg-emerald-950/60 border-emerald-800 text-emerald-300' : 'bg-red-950/60 border-red-800 text-red-300'}`}>
                        <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-[16px]">
                                {message.type === 'success' ? 'check_circle' : 'error'}
                            </span>
                            <span>{message.text}</span>
                        </div>
                        <button onClick={() => setMessage(null)} className="text-slate-400 hover:text-white text-xs">
                            &times;
                        </button>
                    </div>
                )}

                {/* Body Content using Unified FieldMappingDesigner */}
                <div className="flex-1 overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center h-full text-slate-400 text-xs">
                            <span className="material-symbols-outlined animate-spin text-2xl mr-2 text-indigo-400">sync</span>
                            Loading standard mapping preset...
                        </div>
                    ) : (
                        <FieldMappingDesigner
                            key={`${selectedSystem}_${selectedEntityType}`}
                            mode="standard"
                            token={token}
                            standardMapping={activeMappingProp}
                            onSave={handleSavePreset}
                            onResetDefaults={handleResetDefaults}
                            onClose={onClose}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default StandardMappingsModal;
